import { useQuery } from "@apollo/client";
import { useState } from "react";

import { QUERY_CASE_TASKS } from "api/queries/cases";
import { Badge } from "components/ui/badge";
import Card from "pages/CaseDetail/components/Card";
import { RowsSkeleton } from "pages/CaseDetail/components/Skeletons";
import ConsentTaskDialog from "pages/CaseDetail/components/Tasks/ConsentTaskDialog";
import TaskGroup from "pages/CaseDetail/components/Tasks/TaskGroup";
import {
  ConsentAction,
  PendingAction,
  TaskHandlers,
  UploadAction,
} from "pages/CaseDetail/components/Tasks/types";
import UploadTaskDialog from "pages/CaseDetail/components/Tasks/UploadTaskDialog";
import { CaseTask, CaseTasksData } from "pages/CaseDetail/types";

interface TasksProps {
  caseId: string;
  canReopen: boolean;
  onReopen: () => void;
}

const Tasks: React.FC<TasksProps> = ({ caseId, canReopen, onReopen }) => {
  const [confirming, setConfirming] = useState<PendingAction<ConsentAction>>();
  const [uploading, setUploading] = useState<PendingAction<UploadAction>>();

  const { loading, error, data, refetch } = useQuery<CaseTasksData>(
    QUERY_CASE_TASKS,
    {
      variables: { uuid: caseId },
      fetchPolicy: "no-cache",
    },
  );

  const tasks = data?.cases?.case?.tasks ?? [];
  const openTasks = tasks.filter((task) => task.status === "OPEN");
  const closedTasks = tasks.filter((task) => task.status !== "OPEN");

  const isStillOpen = async (task: CaseTask) => {
    const { data: latest } = await refetch();
    return !!latest.cases?.case?.tasks.some(
      ({ uuid, status }) => uuid === task.uuid && status === "OPEN",
    );
  };

  const handlers: TaskHandlers = {
    canReopen,
    onReopen,
    onConsent: (task, action) => setConfirming({ task, action }),
    onUpload: (task, action) => setUploading({ task, action }),
  };

  return (
    <Card
      title="Tasks"
      badge={
        openTasks.length > 0 && (
          <Badge variant="warning">{openTasks.length} to do</Badge>
        )
      }
      aria-busy={loading}
    >
      {loading ? (
        <RowsSkeleton rows={2} />
      ) : error ? (
        <p className="px-6 py-4 text-sm text-muted-foreground">
          Tasks could not be loaded.
        </p>
      ) : !tasks.length ? (
        <p className="px-6 py-4 text-sm text-muted-foreground">
          Nothing to action at this stage.
        </p>
      ) : (
        <div className="divide-y">
          {openTasks.length > 0 && (
            <TaskGroup title="To do" tasks={openTasks} {...handlers} />
          )}
          {closedTasks.length > 0 && (
            <TaskGroup title="Completed" tasks={closedTasks} {...handlers} />
          )}
        </div>
      )}
      <ConsentTaskDialog
        caseId={caseId}
        pending={confirming}
        onClose={() => setConfirming(undefined)}
        onCompleted={() => {
          setConfirming(undefined);
          refetch();
        }}
      />
      <UploadTaskDialog
        caseId={caseId}
        pending={uploading}
        isStillOpen={isStillOpen}
        onClose={() => setUploading(undefined)}
        onCompleted={() => {
          setUploading(undefined);
          refetch();
        }}
      />
    </Card>
  );
};

export default Tasks;
